import React from 'react'
import { Card, CardBody, CardFooter, CardHeader, GridContainer, GridItem, Icons, } from '@fabricks/ui-components'
import { useKeycloak } from 'react-keycloak'
import { get as _get } from 'lodash'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { useGetStatisticsQuery, } from '../api/models'
import { CardElement } from '../components/DashboardItems/CardElement'
import { MaterialMetricCard } from './Metric/MaterialMetric/MaterialMetricCard'
import { BoxTypeMetricCard } from "./Metric/BoxTypeMetric/BoxTypeMetricCard";
import { InvalidMaterialMetricCard } from "./Metric/InvalidMaterialMetric/MaterialMetricCard";
import { PackageTypeMetricCard } from "./Metric/PackageTypeMetric/PackageTypeMetricCard";
import { StorageConfigMetricCard } from "./Metric/StorageConfigMetric/StorageConfigMetricCard";

interface DashboardPageProps {
}

export const Dashboard: React.FC = (props: DashboardPageProps) => {
    const [keycloak] = useKeycloak()

    const { data, loading, error } = useGetStatisticsQuery({
        context: {
            headers: {
                Authorization: keycloak.token ? `Bearer ${keycloak.token}` : 'BLA',
            },
        },
    })

    const materialInactive: number = _get(data, 'getStatistics.materialStat.inActive', 0)
    const materialHasPlace: number = _get(data, 'getStatistics.materialStat.hasPlace', 0)
    const materialNeedToBePlaced: number = _get(data, 'getStatistics.materialStat.needToBePlaced', 0)
    const materialSeries: any = {
        labels: ['Inactive Pieces', ' Pieces have place', 'Pieces need to be placed'],
        series: [[materialInactive, materialHasPlace, materialNeedToBePlaced]],
    }

    const isMaterialValid = () => {
        if (materialSeries.series[0].length <= 0) {
            return (
                <div style={{ color: 'red' }}>
                    <Icons.Warning /> Missing master data values
                </div>
            )
        }
        return (
            <div style={{ color: '#90EE02' }}>
                <Icons.Check /> Valid master data
            </div>
        )
    }

    const pickZonesValue: number = _get(data, 'getStatistics.storageStructureStat.pickZones', -1)
    const zonesValue: number = _get(data, 'getStatistics.storageStructureStat.zones', -1)
    const rackValue: number = _get(data, 'getStatistics.storageStructureStat.racks', -1)
    const levelValue: number = _get(data, 'getStatistics.storageStructureStat.levels', -1)
    const positionsValue: number = _get(data, 'getStatistics.storageStructureStat.positions', -1)
    const sectionsValue: number = _get(data, 'getStatistics.storageStructureStat.sections', -1)
    const customerGroups: number = _get(data, 'getStatistics.storageStructureStat.customerGroups', -1)

    const storageStructureValid = () => {
        if (!pickZonesValue && !zonesValue && !rackValue) {
            return (
                <div style={{ color: 'red' }}>
                    <Icons.Warning /> Missing master data values
                </div>
            )
        }
        return (
            <div style={{ color: '#90EE02' }}>
                <Icons.Check /> Valid master data
            </div>
        )
    }

    const aggregatedResult = _get(data, 'getStatistics.aggregateBoxTypes', [])
    const aggregatedBoxTypeChartData = aggregatedResult.map((boxTypeElement: any) => {
        return {
            name: boxTypeElement.boxType.name,
            count: boxTypeElement.count,
        }
    })

    const isAggregatedBoxTypeValid = () => {
        return (
            <div style={{ color: '#90EE02' }}>
                <Icons.Check /> Valid master data
            </div>
        )
    }

    const freeEntry: number = _get(data, 'getStatistics.storageEntryStat.free', 0)
    const inUseEntry: number = _get(data, 'getStatistics.storageEntryStat.inUse', 0)
    const reservedEntry: number = _get(data, 'getStatistics.storageEntryStat.reserved', 0)

    const pieDataCircle = [
        { name: 'no bin', value: freeEntry },
        { name: 'bin w/o assign', value: inUseEntry },
        { name: 'bin assigned', value: reservedEntry },
    ]
    const COLORS = [ '#fe5570', '#daf967', '#00C49F']

    const renderPieChart = (data: any) => {
        return (
            <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                    <Pie
                        label
                        labelLine
                        isAnimationActive={false}
                        data={data}
                        cy={180}
                        startAngle={180}
                        endAngle={0}
                        innerRadius={100}
                        outerRadius={150}
                        fill="#00FFFF"
                        paddingAngle={5}
                        dataKey="value"
                    >
                        {data.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Legend iconType="circle" wrapperStyle={{ bottom: 20 }} />
                </PieChart>
            </ResponsiveContainer>
        )
    }

    const renderChart = (data: any) => {
        return (
            <ResponsiveContainer width="100%" height={250}>
                <BarChart
                    className='box-type-distribution'
                    height={250}
                    data={data}
                    margin={{
                        top: 10,
                        right: 15,
                        left: -10,
                        bottom: 10,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" stroke="#FFF" tickCount={data.length} tickSize={data.length} angle={-45} textAnchor="end"/>
                    <YAxis stroke="#FFF" />
                    <Tooltip labelStyle={{ color: 'black' }} />
                    <Bar dataKey="count" fill="rgba(254, 85, 112, 1.0)" />
                </BarChart>
            </ResponsiveContainer>
        )
    }

    return (
        <GridContainer>
            <GridItem xs={12} sm={6} md={2}>
                <CardElement
                    title="Pickzones"
                    icon={<Icons.Dashboard />}
                    data={pickZonesValue}
                    valid={storageStructureValid()}
                />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <CardElement
                    title="Zones"
                    icon={<Icons.ZoomOutMap />}
                    data={zonesValue}
                    valid={storageStructureValid()}
                />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <CardElement title="Racks" icon={<Icons.Apps />} data={rackValue} valid={storageStructureValid()} />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <CardElement
                    title="Levels"
                    icon={<Icons.FormatAlignJustify />}
                    data={levelValue}
                    valid={storageStructureValid()}
                />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <CardElement
                    title="Positions"
                    icon={<Icons.LineStyle />}
                    data={positionsValue}
                    valid={storageStructureValid()}
                />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <CardElement
                    title="Sections"
                    icon={<Icons.AllInbox />}
                    data={sectionsValue}
                    valid={storageStructureValid()}
                />
            </GridItem>
            <GridItem xs={12} sm={12} md={8}>
                <Card className="card">
                    <CardHeader className="coloredHeader" color="primary" isStats={false} type="plain">
                        {renderChart(aggregatedBoxTypeChartData)}
                    </CardHeader>
                    <CardBody title="Aggregated box types" align="left">
                        <div>This diagram represents the aggregated box types of the current storage.</div>
                    </CardBody>
                    <CardFooter isStats={false}>{isAggregatedBoxTypeValid()}</CardFooter>
                </Card>
            </GridItem>
            <GridItem xs={12} sm={12} md={4}>
                <Card className="card">
                    <CardHeader className="coloredHeader" color="primary" isStats={false} type="plain">
                        {renderPieChart(pieDataCircle)}
                    </CardHeader>
                    <CardBody title="Storage Stats" align="left">
                        <div>This diagram represents the current situation of your warehouse.</div>
                    </CardBody>
                    <CardFooter isStats={false}>{isMaterialValid()}</CardFooter>
                </Card>
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <CardElement
                    title="Picking Groups"
                    icon={<Icons.People />}
                    data={customerGroups}
                    valid={storageStructureValid()}
                />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <MaterialMetricCard />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <InvalidMaterialMetricCard />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <BoxTypeMetricCard />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <PackageTypeMetricCard />
            </GridItem>
            <GridItem xs={12} sm={6} md={2}>
                <StorageConfigMetricCard />
            </GridItem>
        </GridContainer>
    )
}
